import { zodResolver } from "@hookform/resolvers/zod"
import { ValidationMessage } from "@sop/SEEMP/SeempActionCard"
import { IconButton } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { endpoints, OpModeSchemaWithModes } from "api"
import { Dispatch, SetStateAction } from "react"
import { useForm } from "react-hook-form"
import { MdClear, MdDelete, MdSave } from "react-icons/md"
import { z } from "zod"
import ActionDefinitionCard from "./ActionDefinitionCard"
import NewActionDefinitionComponent from "./NewActionDefinitionComponent"
import { NewGroupForm, SeempActionGroup, SeempSchemaConfigFE } from "./types"

export default function ActionGroupCard({
	schemaId,
	//opModeSchemaId,
	opModeSchemasWithModes,
	actionGroup,
	setSeempSchemas,
}: {
	schemaId: number
	//opModeSchemaId: string
	opModeSchemasWithModes: OpModeSchemaWithModes[]
	actionGroup: SeempActionGroup
	setSeempSchemas: Dispatch<SetStateAction<SeempSchemaConfigFE[]>>
}) {
	const {
		register,
		handleSubmit,
		formState: { isDirty, errors },
		control,
		reset,
		setValue,
		watch,
		trigger,
	} = useForm<z.infer<typeof NewGroupForm>>({
		resolver: zodResolver(NewGroupForm),
		defaultValues: {
			label: actionGroup.label,
			description: actionGroup.description,
			opModeIds: actionGroup.opModesIds,
		},
		mode: "onChange",
	})

	const label = watch("label")
	const description = watch("description")

	const handleActionGroupDelete = (schemaId: number, actionGroupId: number) => async () => {
		if (!window.confirm("Are you sure you want to delete this action group?")) {
			return
		}
		try {
			await requestApi2(endpoints.seempDeleteActionGroup, { id: actionGroupId })
			alert("Action group deleted successfully")
			setSeempSchemas((prev) =>
				prev.map((schema) =>
					schema.id === schemaId
						? {
								...schema,
								actionGroups: schema.actionGroups.filter(
									(group) => group.id !== actionGroupId
								),
							}
						: schema
				)
			)
		} catch (error) {
			console.error("Error deleting action group:", error)
			alert("Error deleting action group")
		}
	}

	const handleActionGroupUpdate = (schemaId: number, actionGroupId: number) => async () => {
		if (label === "" || description === "") {
			alert("Please fill in all fields")
			return
		}
		try {
			await requestApi2(endpoints.seempUpdateActionGroup, {
				actionGroupId,
				label: label === actionGroup.label ? undefined : label,
				description: description === actionGroup.description ? undefined : description,
			})
			alert("Action group updated successfully")
			setSeempSchemas((prev) =>
				prev.map((schema) =>
					schema.id === schemaId
						? {
								...schema,
								actionGroups: schema.actionGroups.map((group) =>
									group.id === actionGroupId
										? {
												...group,
												label: label,
												description: description,
											}
										: group
								),
							}
						: schema
				)
			)
			reset({ label, description })
		} catch (error) {
			console.error("Error updating action group:", error)
			alert("Error updating action group")
		}
	}
	// const opModeOptions = opModeSchemasWithModes
	// 	.find((s) => s.schemaId === opModeSchemaId)
	// 	?.opModes.sort((a, b) => a.name.localeCompare(b.name))
	// 	.map((opMode) => ({
	// 		value: opMode.id,
	// 		label: `${opMode.name} (${opMode.id})`,
	// 	}))

	return (
		<div
			key={actionGroup.id}
			className={`border-dipai-secondary-901/50 flex flex-col gap-4 overflow-hidden rounded-sm border-1 p-4 pt-0`}
		>
			<div
				className={`bg-dipai-secondary-800 -mx-4 flex h-10 flex-row items-center gap-2 pr-4 pl-2`}
			>
				<input
					className={`h-8 w-full rounded-sm border-1 outline-none focus:ring-1 ${errors.label !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-transparent hover:border-white/50 focus:border-white/50"} px-2 text-white`}
					id="label"
					{...register("label")}
				/>
				<IconButton
					size={"medium"}
					variant={"outline-light"}
					onClick={handleActionGroupDelete(schemaId, actionGroup.id)}
				>
					<IconButton.Icon>{MdDelete}</IconButton.Icon>
				</IconButton>
				<IconButton
					disabled={isDirty === false}
					size={"medium"}
					variant={"outline-light"}
					onClick={() => reset()}
				>
					<IconButton.Icon>{MdClear}</IconButton.Icon>
				</IconButton>
				<IconButton
					disabled={
						(label === actionGroup.label && description === actionGroup.description) ||
						label === "" ||
						description === ""
					}
					size={"medium"}
					variant={"outline-light"}
					onClick={handleActionGroupUpdate(schemaId, actionGroup.id)}
				>
					<IconButton.Icon>{MdSave}</IconButton.Icon>
				</IconButton>
			</div>
			<div className={`flex flex-col gap-1.5`}>
				<label
					htmlFor={`description_actionGroup_${actionGroup.id}`}
					className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
				>
					{"description"}
				</label>
				<input
					className={`h-8 w-[calc(100%-6rem)] rounded-sm border-1 px-2 outline-none focus:ring-1 ${errors.description !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
					id={`description_actionGroup_${actionGroup.id}`}
					{...register("description")}
				/>
				{errors.description?.message !== undefined ? (
					<ValidationMessage
						type={"error"}
						validationMessage={errors.description.message}
					/>
				) : null}
			</div>
			{/* <div className={`flex flex-col gap-1.5`}>
				<label
					htmlFor={`op_modes_actionGroup_${actionGroup.id}`}
					className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
				>
					{"op modes"}
				</label>
				<Controller
					name="opModeIds"
					control={control}
					rules={{
						required: "Please select at least an operation mode",
					}}
					render={({ field, fieldState }) => {
						const selected = opModeOptions?.filter((o) =>
							field.value?.includes(o.value)
						)
						return (
							<ReactSelect
								{...field}
								id={`op_modes_actionGroup_${actionGroup.id}`}
								className="min-w-100"
								options={opModeOptions}
								placeholder={"select the op modes..."}
								styles={selectComponentStyles(errors.opModeIds?.message, false)}
								isMulti={true}
								value={selected}
								onChange={(mv: MultiValue<{ value: number; label: string }>) =>
									field.onChange(mv.map((o) => o.value))
								}
								isSearchable={true}
								hideSelectedOptions={false}
								closeMenuOnSelect={false}
								isClearable={true}
								menuPlacement="auto"
								menuPortalTarget={document.body}
							/>
						)
					}}
				/>
				{errors.opModeIds?.message !== undefined ? (
					<ValidationMessage
						type={"error"}
						validationMessage={errors.opModeIds.message}
					/>
				) : null}
			</div> */}
			{/* NEW ACTION DEFINITION SECTION */}
			<NewActionDefinitionComponent
				schemaId={schemaId}
				actionGroupId={actionGroup.id}
				setSeempSchemas={setSeempSchemas}
			/>
			{/* ACTIONS DEFINITIONS SECTION */}
			{actionGroup.actionsDefinitions.length > 0 ? (
				<div className={`grid grid-cols-1 gap-4 xl:grid-cols-2`}>
					{actionGroup.actionsDefinitions.map((action) => (
						<ActionDefinitionCard
							key={action.id}
							schemaId={schemaId}
							actionGroupId={actionGroup.id}
							actionDefinition={action}
							setSeempSchemas={setSeempSchemas}
						/>
					))}
				</div>
			) : null}
		</div>
	)
}
